import { LocalizationString } from '@celito.clients/assets';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  isAxiosError,
} from 'axios';
import { isEqual, upperCase } from 'lodash';
import { match } from 'ts-pattern';

import { environment } from '../config/environment';
import {
  hasExceededLoginAttempts,
  recordLoginAttempt,
} from '../config/login-loop-guard';
import { getTenantConfigApiBase } from '../config/tenant-config';
import { ApiMethodType } from '../enums/api-constants';
import { Constants } from '../enums/constants';
import {
  HTTPContentType,
  HTTPHeader,
  HTTPStatusCode,
} from '../enums/http-constants';
import { ROUTES } from '../enums/routes';
import { logger } from '../services/logger';
import { Environment } from '../singleton/environment-manager';
import MsalInstanceManager from '../singleton/msal-instance-manager';

// Seed the Environment singleton with build-time values other modules still
// read. This is NOT one of the values WI-F5 converts (baseURL / 401 targets);
// it is preserved because other modules rely on the seeding happening here.
Environment.getInstance().setEnvironment(environment);

// ---------------------------------------------------------------------------
// WI-F5 — API base URL, resolved at RUNTIME (not at module import).
// ---------------------------------------------------------------------------
//
// TODO(US-8.4): FE↔API host convention unresolved.
//
// Per reconciliation R1 the API is NOT one baked-in host: tenants live on
// per-tenant subdomains / vanity domains behind Front Door, so a single
// build-time `NX_API_BASE_URL` cannot reach every tenant's API. Default here is
// SAME-ORIGIN — consistent with F1's `/config` same-origin decision
// (`getTenantConfigApiBase()` returns '' for same-origin). The stable `/api/v1`
// prefix is appended HERE because endpoint paths (`config/endpoint.ts`, e.g.
// `/user`) are prefix-relative — so a same-origin request to `/user` becomes
// `/api/v1/user` against the current tenant's origin. This is the ONE
// clearly-marked place infra overrides once the host convention settles
// (US-8.4). Do NOT hardcode a tenant host.
const API_PATH_PREFIX = '/api/v1';

/**
 * Resolve the axios base URL at runtime. Same-origin default → `/api/v1`
 * (host portion comes from {@link getTenantConfigApiBase}; empty = same-origin).
 */
export const resolveApiBaseUrl = (): string =>
  `${getTenantConfigApiBase()}${API_PATH_PREFIX}`;

// ---------------------------------------------------------------------------
// WI-F5 — 401 redirect targets, host-relative (no baked-in host).
// ---------------------------------------------------------------------------
//
// Previously build-time full URLs (`NX_NOT_INVITED_URL` /
// `NX_NOT_ACTIVE_USER_URL`, e.g. `http://localhost:4200/not-invited?isPublic=true`).
// Now host-relative routes so they resolve against the CURRENT tenant domain at
// `window.location.replace` time, working per-tenant without a baked-in host.
const withPublicFlag = (route: ROUTES): string =>
  `${route}?${Constants.IS_PUBLIC}=true`;

/** Host-relative 401 "not invited" redirect target. */
export const getNotInvitedRedirectUrl = (): string =>
  withPublicFlag(ROUTES.NOT_INVITED);

/** Host-relative 401 "deactivated user" redirect target. */
export const getNotActiveUserRedirectUrl = (): string =>
  withPublicFlag(ROUTES.NOT_ACTIVE_USER);

/** * Create an Axios Client with defaults. baseURL is resolved at runtime in
 * {@link initAxios} — NOT at module import — so no config is read eagerly. */
const axiosInstance = axios.create();

export const initAxios = () => {
  // Resolve the API base at boot time (after the tenant-config fetch), never at
  // module import — keeps this module free of import-time config side effects.
  axiosInstance.defaults.baseURL = resolveApiBaseUrl();
  setupAxiosHeader();
  setAxiosResponseInterceptor();
};

const setupAxiosHeader = () => {
  axiosInstance.defaults.headers.common[HTTPHeader.ACCEPT] =
    HTTPContentType.APPLICATION_JSON;
  axiosInstance.defaults.headers.common[HTTPHeader.CONTENT_TYPE] =
    HTTPContentType.APPLICATION_JSON;
};

const shouldLog = (error: AxiosError) => {
  return (
    !isEqual(error.code, 'ERR_CANCELED') &&
    (!error.status || /^5\d{2}$/.test(error.status.toString()))
  );
};

const setAxiosResponseInterceptor = () => {
  const handleErr = (error: Error) => {
    if (!isAxiosError(error)) {
      logger.error(error, {
        origin: 'AxiosResponseInterceptor',
      });
      return Promise.reject(error);
    }
    if (shouldLog(error))
      logger.error(error, {
        origin: 'AxiosResponseInterceptor',
        ...error.toJSON(),
      });

    const res = error.response;
    if (res?.data.statusCode === HTTPStatusCode.UNAUTHORIZED) {
      // Check if logout is already in progress to prevent race conditions
      const msalManager = MsalInstanceManager.getInstance();
      if (msalManager.getLoggingOut()) {
        return Promise.reject(error);
      }

      match(res.data.errorMessage)
        .with(LocalizationString.USER_NOT_INVITED, () => {
          window.location.replace(getNotInvitedRedirectUrl());
        })
        .with(LocalizationString.USER_IS_DEACTIVATED, () => {
          window.location.replace(getNotActiveUserRedirectUrl());
        })
        .with(LocalizationString.USER_DOES_NOT_HAVE_ACCESS, () => {
          // Don't force logout for permission errors - user is authenticated but lacks access
          logger.info('User does not have access to resource', {
            origin: 'AxiosResponseInterceptor',
          });
        })
        .otherwise(() => {
          // Only trigger login redirect if it's a genuine auth error
          // Don't trigger on transient network issues that return 401
          if (
            res.data.errorMessage &&
            (res.data.errorMessage.includes('token') ||
              res.data.errorMessage.includes('auth') ||
              res.data.errorMessage.includes('session'))
          ) {
            // Bounded by the same budget as the boot-time redirect (ADO #38557).
            // A 401 that survives re-authentication — a stale audience or issuer
            // in the tenant registry, say — otherwise bounces here forever:
            // redirect, return with a valid account, call the API, 401, repeat.
            // The boot guard never saw this path because it counts only its own
            // redirects.
            if (hasExceededLoginAttempts()) {
              logger.error(
                new Error(
                  'Suppressed a 401 login redirect: the automatic redirect ' +
                    'budget for this session is exhausted.'
                ),
                {
                  origin: 'AxiosResponseInterceptor',
                  errorMessage: res.data.errorMessage,
                }
              );
              return Promise.reject(error);
            }

            logger.info('Authentication error detected, redirecting to login', {
              origin: 'AxiosResponseInterceptor',
              errorMessage: res.data.errorMessage,
            });
            recordLoginAttempt();
            msalManager.setLoggingOut(true);
            MsalInstanceManager.getInstance().getMsalInstance().loginRedirect({
              scopes: msalManager.getApiScopes(),
            });
          } else {
            // Log but don't force logout for other 401 errors
            logger.info('Unauthorized request, but not forcing logout', {
              origin: 'AxiosResponseInterceptor',
              errorMessage: res.data.errorMessage,
            });
          }
        });
    }

    return Promise.reject(error);
  };

  axiosInstance.interceptors.response.use((response) => response, handleErr);
};

export const setAxiosAuthToken = (token: string) => {
  axiosInstance.defaults.headers.common[HTTPHeader.AUTHORIZATION] =
    `Bearer ${token}`;
};

export const removeAxiosAuthToken = () => {
  axiosInstance.interceptors.request.clear();
};

export const performRequest = async <T>(
  url: string,
  method: ApiMethodType,
  data: object | null = null,
  params?: object,
  config?: AxiosRequestConfig,
  signal?: AbortSignal
): Promise<AxiosResponse<T>> => {
  logger.info(`${upperCase(method)} Request`, { type: 'http', url, method });

  const token = await MsalInstanceManager.getInstance().getTokenSilently();
  if (token) {
    setAxiosAuthToken(token);
  } else {
    // Token acquisition failed, but don't make the request without a token
    throw new Error('Failed to acquire access token');
  }

  switch (method) {
    case ApiMethodType.GET:
      return getApi(url, params, config, signal);
    case ApiMethodType.POST:
      return postApi(url, data, params, config, signal);
    case ApiMethodType.PUT:
      return putApi(url, data, params, config);
    case ApiMethodType.DELETE:
      return deleteApi(url, data, params, config);
    case ApiMethodType.PATCH:
      return patchApi(url, data, params, config);
  }
};

const getApi = async (
  url: string,
  params?: object,
  config?: AxiosRequestConfig,
  signal?: AbortSignal
) => {
  return axiosInstance.get(url, { params, ...config, signal });
};

const postApi = async (
  url: string,
  data: object | null,
  params?: object,
  config?: AxiosRequestConfig,
  signal?: AbortSignal
) => {
  // data is optional for post requests. It is not used for put and delete
  return axiosInstance.post(url, data, { params, ...config, signal });
};

const putApi = async (
  url: string,
  data: object | null,
  params?: object,
  config?: AxiosRequestConfig
) => {
  return axiosInstance.put(url, data, { params, ...config }); // data is optional for post requests.  It is not used for put and delete;
};

const deleteApi = async (
  url: string,
  data?: object | null,
  params?: object,
  config?: AxiosRequestConfig
) => {
  // data is optional for put and delete
  return axiosInstance.delete(url, { params, ...config, data });
};

const patchApi = async (
  url: string,
  data: object | null,
  params?: object,
  config?: AxiosRequestConfig
) => {
  return axiosInstance.patch(url, data, { params, ...config });
};

export const performFormRequest = async (
  url: string,
  method: ApiMethodType,
  data: object | null,
  params: object = {},
  config?: AxiosRequestConfig
) => {
  logger.info(`${upperCase(method)} Request`, { type: 'http', method, url });
  // data is optional for post requests.  It is not used for put and delete
  return performRequest(
    url,
    method,
    data,
    {
      ...params,
      headers: { ContentType: 'multipart/form-data' },
    },
    data instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' }, ...config }
      : undefined
  ); // data is optional for post requests.  It is not used for put and delete;
};
export default axiosInstance;
